import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import {
  FormControl,
  Snackbar,
  Alert,
} from "@mui/material";
import "./manageStockAllocation.css";
import { Loader } from "../../components/loader/Loader";
import CustomDataGrid from "../../components/dms_common/CustomDataGrid";
import HeaderV2 from "../../components/dms_common/HeaderV2";
import GenericSummaryCard, { GenericSummaryCardItemObject } from "../../components/dms_common/GenericSummaryCard";
import { formatAmount, getQuantityType } from "../../components/dms_common/DmsUtils";
import ScreenOverlay from "../../components/dms_common/ScreenOverlay";
import TextSwitcher, { TextSwitcherItemObject } from "../../components/dms_common/TextSwitcher";
import { createSkuWiseStockMapForBatchId, GenericModal, getBatchCodeQuantities, getBatchCodes, getBatchCodesWithoutFreeItem, getGenericModalProps, getSkuToNameCodeMap, getSkuToReplacementSkuMap, initialGenericModalProps, processSkuFulfillment, processSkuFulfillmentWithProduct, } from "../viewOrder/ViewOrderUtils";
import CustomGenericModal from "../../components/dms_common/CustomGenericModal";
import FormHeader from "../../components/dms_common/FormHeader";
import SearchV2 from "../../components/dms_common/SearchV2";
import { addToUomWiseAllocation, buildStockAllocationData, checkAllocation, fetchStockDataForOrders, generateGlobalNormalizedStockMap, generateOrderBodyByBatchMap, generateOrderWiseAllocationData, getGlobalStockMap, getMetaData1, getOrderBodyForPayload, GlobalNormalizedStockMap, GlobalStockMapRecord, OrderBodyForPayloadMap, OrderWiseAllocationData, SkuToOrderMapping, StockAllocationRowData, verifyAllocation } from "./ManageStockAllocationUtils";
import CustomAccordianV2 from "../../components/dms_common/CustomAccordianV2";
import { filterData, getMultipleFilterBasedData, searchWithStatusFilter } from "../../components/dms_common/DmsCommonServices";
import { capitalizeFirstLetter } from "../../utils/utils";
import { getConfigData } from "../../services/configurationService";
import { fetchBatchDataForSkuList } from "../stocks/RealTimeStock/RealTimeStockUtils";
import { getDataFromLocalDb } from "../../services/dbService";
import { performUomConversion } from "../../services/catalogueService";
import { getOutletDetails, getOutletDetailsForSecondary } from "../createSecondaryOrder/CreateSecondaryOrderService";
import { OutletDetails } from "../createSecondaryOrder/models";
import OutletWiseAllocationModal from "../../components/modals/OutletWiseAllocationModal";
import BulkInvoiceModal from "./BulkInvoiceModal";
import { orderStatusConfig } from "../../types/types";
import { L, R } from "ace-builds-internal/lib/bidiutil";
import { GridRenderCellParams, GridTreeNodeWithRender } from "@mui/x-data-grid";
import { ReplacementPopoverCell } from "./ReplacementPopOverCell";
import { globalHandlersIntegration } from "@sentry/react";
// ✅ Dummy data interfaces


// ✅ Column metadata with dropdown
const getStockAllocationColumnsMetaData = (
  handleDropDownSelect: (newValue: string, row: StockAllocationRowData) => void
) => ({
  columns: [
    {
      headerName: "SKU Details",
      mappedValue: "skuCode,skuName",
      width: 180,
      headerAlign: "left",
      align: "left",
      truncateText: true,
    },
    {
      headerName: "Outlet Ordered",
      headerParts: { top: "Outlet ", bottom: "Ordered" },
      mappedValue: "outletName,outletCode",
      width: 150,
      headerAlign: "left",
      align: "left",
      type: "viewDropDown",
      keyAccountShow: true,
      truncateText: true,
    },
    {
      headerName: "Ordered Quantity",
      headerParts: { top: "Ordered ", bottom: "Qty" },
      mappedValue: "orderedCases,orderedPieces,orderedOthers",
      width: 180,
      headerAlign: "left",
      align: "left",
      includeQty: true,
      includeStockCheck: true,
      type: "order",
    },
    {
      headerName: "Available Qty",
      headerParts: { top: "Available ", bottom: "Qty" },
      mappedValue: "availableCases,availablePieces,availableOthers",
      width: 180,
      headerAlign: "left",
      align: "left",
      includeQty: true,
      type: "stock",
    },
    {
      headerName: "Final Allocation",
      headerParts: { top: "Final ", bottom: "Allocation" },
      mappedValue: "allocatedCases,allocatedPieces,allocatedOthers",
      width: 180,
      headerAlign: "left",
      align: "left",
      includeQty: true,
      showOverSoldQuantity: true,
      type: "remaining",
    },
    {
      headerName: "Allocated method",
      field: "allocationMethod", // ✅ key for dropdown binding
      mappedValue: "",
      headerParts: { top: "Allocated ", bottom: "method" },
      handleDropDownSelect,
      dropdownValues: ["FIFO", "MANUAL"],
      width: 130,
      headerAlign: "left",
      align: "left",
      type: "dropdown",
    },
    {
      headerName: "Replacement SKU",
      fields: "replacementSkuAllocated",
      headerParts: { top: "Replacement", bottom: "SKU" },
      mappedValue: "replacementSkuAllocated",
      width: 130,
      type: "popOver"
    },
    {
      headerName: "Status",
      mappedValue: "status",
      width: 150,
      headerAlign: "left",
      align: "left",
    },
  ],
  Actions: ["view"],
});



const ManageStockAllocation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const selectedOrders = location.state?.selectedOrders || [];

  // Configuration from localStorage
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const currencySymbol = ConfigDictionary.currency || "₹";

  // State management
  const [loader, setLoader] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [data, setData] = useState<StockAllocationRowData[]>([]);
  const [manageStockAllocationRows, setManageStockAllocationRows] = useState<StockAllocationRowData[]>([]);
  const [searchedValue, setSearchedValue] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({"status": ["PARTIALLY ALLOCATED", "OUT OF STOCK"]});
  const [multipleFiltersVisible, setMultipleFiltersVisible] = useState<boolean>(false);
  const [multipleFiltersList, setMultipleFiltersList] = useState<Record<string, Record<string, number>>>({});
  const [selectedFilterType, setSelectedFilterType] = useState("allocationStatus");
  const [filterSearchValue, setFilterSearchValue] = useState<string>("");
  const [resetSearch, setResetSearch] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Dayjs>(dayjs());
  const [checkedRows, setCheckedRows] = useState<string[]>([]);
  const [genericModalProps, setGenericModalProps] = useState<GenericModal>(
        initialGenericModalProps
      );
  const orderUomCheck = useRef<string>("");
  const [accordionOrderData, setAccordionOrderData] = useState<any>({});
  const [stockAllocationModalOpen, setStockAllocationModalOpen] = useState<boolean>(false);
  const [bulkInvoiceModalOpen, setBulkInvoiceModalOpen] = useState<boolean>(false);
  const [completeOrderBody, setCompleteOrderBody] = useState<OrderBodyForPayloadMap>({});
  const [skuWiseData, setSkuWiseData] = useState<any>({})
  const [skuWiseRowData, setSkuWiseRowData] = useState<OrderWiseAllocationData[]>([]);
  const enableSKUReplacement = useRef<boolean>(false);

  const [bulkInvoiceModalRows, setBulkInvoiceModalRows] = useState<any[]>([]);
  const [bulkInvoiceModalLoader, setBulkInvoiceModalLoader] = useState<boolean>(false);
  // Summary card data
  const [orderBodyForPayload, setOrderBodyForPayload] = useState<any>({});
  const [orderData, setOrderData] = useState<any>();
  const [filterKeys, setFilterKeys] = useState<string[]>(["status"]); 
  const [bulkOrderTrackingStages, setBulkOrderTrackingStages] = useState<
      orderStatusConfig[]
    >([]);  
  const [isAnyManualAndPending, setIsAnyManualAndPending] = useState<boolean>(false)
  const [globalNormalizedStockMap, setGlobalNormalizedStockMap] = useState<GlobalNormalizedStockMap>();
  const productCatalogue = useRef<any>({});
    
  const fetchRequiredConfigs = async () => {
    try {
      const filterKeysConfig = await getConfigData("dms_configuration","multiFilterKeysManageOrders");
      setFilterKeys(filterKeysConfig[0]?.configValue ?? ["status", "salesRepName"]);
      ;
      const orderUomCheckConfig = await getConfigData("catalogue_configuration", "orderUomCheck");
      orderUomCheck.current = orderUomCheckConfig[0]?.configValue ?? "";
      const res20 = await getConfigData("dms_configuration", "enableSKUReplacement");
      enableSKUReplacement.current = res20[0]?.configValue ?? "";

      const orderTrackingStages = await getConfigData(
        "order_configuration",
        "orderTrackingStages"
      );
      setBulkOrderTrackingStages(orderTrackingStages[0]?.configValue ?? []);
      const dbResult = await getDataFromLocalDb("Product");
      const products: any = dbResult?.data?.[0]?.data ?? {};
      productCatalogue.current = products
    } catch (e) {
      console.error("Error while fetching configs", e);
    }
  };
  // Order status configuration (dummy)
  const orderTrackingStagesList = [
    { id: "ALL", dmsLabel: "All", dmsLabelColor: "#B96D16", "dmsColor": "#FFF5E9", },
    { id: "FULLY ALLOCATED", dmsLabel: "Fully allocated", dmsLabelColor: "#23C65B", "dmsColor": "#F3FCF3", },
    { id: "PARTIALLY ALLOCATED", dmsLabel: "Partially allocated", dmsLabelColor: "#b96d16", "dmsColor": "#fff5e9", },
    { id: "OUT OF STOCK", dmsLabel: "Out of stock", dmsLabelColor: "#C44C36", "dmsColor": "#FFF9F8", },
  ];

const handleDropDownSelect = async (newValue: string, row: StockAllocationRowData) => {
  const showEyeIcon = newValue.toUpperCase() === "FIFO";
  const showEditIcon = newValue.toUpperCase() === "MANUAL";

setGlobalNormalizedStockMap((prev: GlobalNormalizedStockMap | undefined) => {
  // create a shallow copy of the Map so we don't mutate prev
  const next = prev ? new Map(prev) : new Map<string, GlobalStockMapRecord>();

  // read existing record (may be undefined)
  const existing = next.get(row.batchCode) ?? ({} as GlobalStockMapRecord);

  // create a new record object (shallow copy) with updated fields
  const updated: GlobalStockMapRecord = {
    ...existing,
    allocationMethod: (newValue ?? "").toString().toUpperCase(),
    showEditIcon,
    // only set hasCompletedAllocation if showEyeIcon; otherwise keep existing value
    ...(showEyeIcon ? { hasCompletedAllocation: false } : {}),
  };

  next.set(row.batchCode, updated);
  return next;
});


  // // 1️⃣ Update UI rows
  // setData(prevRows =>
  //   prevRows.map(r => {
  //     if (r.id !== row.id) return r;

  //     return {
  //       ...r,
  //       allocationMethod: newValue,
  //       showEditIcon,
  //       // if switching to FIFO we explicitly mark completed false; otherwise preserve existing
  //       hasCompletedAllocation: showEyeIcon ? false : r.hasCompletedAllocation,
  //     };
  //   })
  // );

  // // 2️⃣ Update manageStockAllocationRows similarly
  // setManageStockAllocationRows(prevRows =>
  //   prevRows.map(r => {
  //     if (r.id !== row.id) return r;

  //     return {
  //       ...r,
  //       allocationMethod: newValue,
  //       showEditIcon,
  //       // if switching to FIFO we explicitly mark completed false; otherwise preserve existing
  //       hasCompletedAllocation: showEyeIcon ? false : r.hasCompletedAllocation,
  //     };
  //   })
  // );

  if (showEditIcon) {
    handleRowClick(row)
  }
  if (newValue === "FIFO") handleFifoSelectionAll(row.batchCode)
  handleReset(row?.batchCode);
};

const handleFifoSelectionAll = (selectedBatchCode: string) => {
  setGlobalNormalizedStockMap((prevState) => {
    const prev = prevState ?? new Map<string, GlobalStockMapRecord>();
    const next = new Map(prev); // clone map reference for React

    // helper: apply FIFO reset/recompute logic to a skuCode in `next`
    const processSkuForFifo = (batchCode: string) => {
      const currentSku = next.get(batchCode);
      if (!currentSku) return;

      const clonedSku: GlobalStockMapRecord = {
        ...currentSku,
        replacementSkuAllocated: { ...(currentSku.replacementSkuAllocated ?? {}) },
        orderedQuantityByOrder: { ...(currentSku.orderedQuantityByOrder ?? {}) },
        mainAllocatedByOrder: { ...(currentSku.mainAllocatedByOrder ?? {}) },
        initialMainAllocatedByOrder: { ...(currentSku.initialMainAllocatedByOrder ?? {}) },
        orders: Array.isArray(currentSku.orders) ? [...currentSku.orders] : [],
      };

      // free main pool into replacement pool
      const freed = Number(clonedSku.mainAllocatedQuantity ?? 0);
      clonedSku.replacementQuantity = (clonedSku.replacementQuantity ?? 0) + freed;
      clonedSku.remainingReplacementQuantity = (clonedSku.remainingReplacementQuantity ?? 0) + freed;
      clonedSku.oversoldQuantity = Math.max((clonedSku.oversoldQuantity ?? 0) + freed, 0);
      clonedSku.mainAllocatedQuantity = 0;
      clonedSku.initialMainAllocatedByOrder = {};
      clonedSku.mainAllocatedByOrder = {};

      // 1) revert previous replacement allocations that were attributed to this SKU
      const prevReplacementMapForThisSku = currentSku.replacementSkuAllocated ?? {};
      for (const [orderId, replMap] of Object.entries(prevReplacementMapForThisSku)) {
        if (!replMap) continue;
        for (const [replSkuCode, qtyValue] of Object.entries(replMap)) {
          const qty = Number(qtyValue ?? 0);
          if (!qty) continue;
          const replRecord = next.get(replSkuCode);
          if (!replRecord) continue;

          const clonedRepl: GlobalStockMapRecord = { ...replRecord };
          clonedRepl.allocatedReplacementQuantity = Math.max(
            0,
            (clonedRepl.allocatedReplacementQuantity ?? 0) - qty
          );
          clonedRepl.remainingReplacementQuantity = Math.max(
            0,
            (clonedRepl.remainingReplacementQuantity ?? 0) + qty
          );

          next.set(replSkuCode, clonedRepl);
        }
      }

      // 2) Clear per-order replacement allocations on this SKU
      clonedSku.replacementSkuAllocated = {};

      // 3) Recompute mainAllocatedByOrder (FIFO) using the freed physicalMainPool
      const ordersList: any[] = Array.isArray(clonedSku.orders) ? clonedSku.orders.slice() : [];
      ordersList.sort((a, b) => new Date(a.creationTime).getTime() - new Date(b.creationTime).getTime());

      let remainingMainStock = Number(clonedSku.remainingReplacementQuantity ?? 0);
      const recomputedMainByOrder: Record<string, number> = {};
      const stockExceedingMap: Record<string, number> = {};

      for (const ord of ordersList) {
        const orderId = String(ord.id);
        const orderedQtyForThisSku = Number(clonedSku.orderedQuantityByOrder?.[orderId] ?? 0);

        if (!orderedQtyForThisSku || remainingMainStock <= 0) {
          recomputedMainByOrder[orderId] = 0;
          if (orderedQtyForThisSku > remainingMainStock) {
            stockExceedingMap[orderId] = orderedQtyForThisSku - Math.max(remainingMainStock, 0);
          }
          continue;
        }

        if (orderedQtyForThisSku > remainingMainStock) {
          stockExceedingMap[orderId] = orderedQtyForThisSku - remainingMainStock;
        }

        const allocate = Math.min(orderedQtyForThisSku, remainingMainStock);
        recomputedMainByOrder[orderId] = allocate;
        remainingMainStock -= allocate;
      }

      clonedSku.mainAllocatedByOrder = recomputedMainByOrder;

      // 4) recompute mainAllocatedQuantity and adjust replacement pools
      const recomputedMainAllocatedTotal = Object.values(recomputedMainByOrder).reduce(
        (s, v) => s + (Number(v) || 0),
        0
      );
      clonedSku.mainAllocatedQuantity = recomputedMainAllocatedTotal;
      clonedSku.replacementQuantity = Math.max((clonedSku.replacementQuantity ?? 0) - recomputedMainAllocatedTotal, 0);
      clonedSku.remainingReplacementQuantity = Math.max((clonedSku.remainingReplacementQuantity ?? 0) - recomputedMainAllocatedTotal, 0);
      clonedSku.oversoldQuantity = Math.max((clonedSku.oversoldQuantity ?? 0) - recomputedMainAllocatedTotal, 0);

      // 5) allocate replacements for orders that still need qty (stockExceedingMap)
      const replList = Array.isArray(clonedSku.replacementSku) ? clonedSku.replacementSku : [];
      clonedSku.replacementSkuAllocated = clonedSku.replacementSkuAllocated ?? {};

      for (const [orderId, exceedingQtyRaw] of Object.entries(stockExceedingMap)) {
        let remainingToFill = Number(exceedingQtyRaw ?? 0);
        if (!remainingToFill || remainingToFill <= 0) continue;

        const perOrderAlloc: Record<string, number> = { ...(clonedSku.replacementSkuAllocated?.[orderId] ?? {}) };

        for (const replCode of replList) {
          if (remainingToFill <= 0) break;
          const replRecord = next.get(replCode);
          if (!replRecord) continue;

          const replRemaining = Number(replRecord.remainingReplacementQuantity ?? 0) - (replRecord.oversoldQuantity ?? 0);
          if (replRemaining <= 0) continue;

          const allocate = Math.min(replRemaining, remainingToFill);

          const clonedRepl = { ...replRecord };
          clonedRepl.allocatedReplacementQuantity = (clonedRepl.allocatedReplacementQuantity ?? 0) + allocate;
          clonedRepl.remainingReplacementQuantity = Math.max(0, (clonedRepl.remainingReplacementQuantity ?? 0) - allocate);
          next.set(replCode, clonedRepl);

          perOrderAlloc[replCode] = (perOrderAlloc[replCode] ?? 0) + allocate;
          remainingToFill -= allocate;
        }

        clonedSku.replacementSkuAllocated[orderId] = perOrderAlloc;
      }

      // 6) Reset allocation flags / method
      clonedSku.allocationMethod = "FIFO";
      clonedSku.hasCompletedAllocation = false;

      // 7) write back cloned sku
      next.set(batchCode, clonedSku);
    };

    // 1) ALWAYS process the selected SKU first
    processSkuForFifo(selectedBatchCode);

    // 2) Then process all other SKUs that currently have allocationMethod === "FIFO"
    prev.forEach((skuRecord, skuCode) => {
      if (skuCode === selectedBatchCode) return; // skip selected
      if (skuRecord?.allocationMethod !== "FIFO") return;
      processSkuForFifo(skuCode);
    });

    return next;
  });
};



const handleReset = (batchCode: string) => {
  // 1️⃣ Update UI rows
//   setData((prevRows) =>
//   prevRows.map((r) =>
//     r.batchCode === batchCode || r.id === batchCode
//       ? {
//           ...r,
//           allocationMethod: "FIFO",
//           showEyeIcon: true,
//           hasCompletedAllocation: false,
//           order: r.order?.map((o: any) => ({
//             ...o,
//             orderDetails: o.orderDetails?.map((od: any) =>
//               od.batchCode === batchCode
//                 ? {
//                     ...od,
//                     allocatedCases: 0,
//                     allocatedPieces: 0,
//                     allocatedOthers: 0,
//                     hasBeenManuallyAllocated: false,
//                   }
//                 : od
//             ),
//           })),
//         }
//       : r
//   )
// );
//   // 2️⃣ Reset manageStockAllocationRows
// setManageStockAllocationRows((prevRows) =>
//   prevRows.map((r) =>
//     r.batchCode === batchCode || r.id === batchCode
//       ? {
//           ...r,
//           allocationMethod: "FIFO",
//           showEyeIcon: true,
//           hasCompletedAllocation: false,
//           order: r.order?.map((o: any) => ({
//             ...o,
//             orderDetails: o.orderDetails?.map((od: any) =>
//               od.batchCode === batchCode
//                 ? {
//                     ...od,
//                     allocatedCases: 0,
//                     allocatedPieces: 0,
//                     allocatedOthers: 0,
//                     hasBeenManuallyAllocated: false,
//                   }
//                 : od
//             ),
//           })),
//         }
//       : r
//   )
// );

  // 2️⃣ Update payload (orderBodyForPayload)
  if (orderBodyForPayload) {
    const { auto: existingAuto, manual: existingManual } = orderBodyForPayload;

    const newAuto = [...existingAuto];
    const newManual = existingManual
      .map((order: any) => {
        // remove the batchCode from orderDetails
        const updatedDetails = order.orderDetails.filter(
          (od: any) => od.batchCode !== batchCode
        );
        return { ...order, orderDetails: updatedDetails };
      })
      // remove manual orders that now have no orderDetails
      .filter((order: any) => order.orderDetails.length > 0);

    // any removed orders go back to auto
    existingManual.forEach((order:any) => {
      const hadBatch = order.orderDetails.some((od: any) => od.batchCode === batchCode);
      if (hadBatch && !newAuto.includes(order.orderNumber)) newAuto.push(order.orderNumber);
    });
    console.log("Resetted Order Payload", { auto: newAuto, manual: newManual })
    setOrderBodyForPayload({ auto: newAuto, manual: newManual });
  }

  if (completeOrderBody) {
    const newOrderBody = { ...completeOrderBody };
    Object.values(newOrderBody).forEach((order: any) => {
      if (order.orderDetails[batchCode]) {
        const batchNode = order.orderDetails[batchCode];
        // reset allocated quantities and manual allocation flag
        order.orderDetails[batchCode] = {
          ...batchNode,
          allocatedCases: 0,
          allocatedPieces: 0,
          allocatedOthers: 0,
          hasBeenManuallyAllocated: false,
        };
      }
    });
    setCompleteOrderBody(newOrderBody);
    console.log("Resetted Order Body", newOrderBody)
  }

  if (stockAllocationModalOpen){
    setStockAllocationModalOpen(false);
  }
};



  useEffect(() => {
    const run = async () => {
      await fetchRequiredConfigs()
      if (!selectedOrders || selectedOrders.length <= 0) return

      const orderBodyForPayload = getOrderBodyForPayload(selectedOrders);
      console.log("inital payload", orderBodyForPayload)
      setOrderBodyForPayload(orderBodyForPayload)

      const batchKeyedOrderBody = generateOrderBodyByBatchMap(selectedOrders);
      setCompleteOrderBody(batchKeyedOrderBody);
      setOrderData(selectedOrders);
    }
    run()
  }, [selectedOrders])

  const [allocationError, setAllocationError] = useState(false);

  useEffect(() => {
    const run = async () => {
      if (!orderData || orderData.length <= 0) return;
      setLoader(true);
      try {
        const [globalNormalizedStockMap, stocksData] = await getGlobalStockMap(orderData)
        const globalStockMap = await generateGlobalNormalizedStockMap(orderData, globalNormalizedStockMap, stocksData, orderUomCheck.current)
        setGlobalNormalizedStockMap(globalStockMap);

      } catch(error) {
        console.log(error)
      } finally{
        setLoader(false);
      }
    }
    run()
  }, [orderData, allocationError])


  useEffect(() => {
    const run = async () => {
      if (!orderData || allocationError) return; // prevent retry loop
      setLoader(true);
      try {
        if (globalNormalizedStockMap){
          console.log(globalNormalizedStockMap)
          const data = await buildStockAllocationData(orderData, globalNormalizedStockMap, orderUomCheck.current);
          setData(data);
          setManageStockAllocationRows(data);
          setLastRefreshed(dayjs());
        } 
      } catch (error: any) {
        console.error("error", error);
        setAllocationError(true); // block future retries
        setGenericModalProps(getGenericModalProps("error", "Something went wrong"));
      } finally {
        setLoader(false);
      }
    };
    run();
  }, [globalNormalizedStockMap]);

  const handleRowClick = async (row: any) => {
    const orderList = row?.order ?? [];
    const batchCode = row?.batchCode;
    const outletList = row?.outletList;
    try{
      await verifyAllocation(orderBodyForPayload)
      if (globalNormalizedStockMap){
        const data = await generateOrderWiseAllocationData(orderList, globalNormalizedStockMap,batchCode, orderUomCheck.current,outletList)
        setSkuWiseData(row)
        setSkuWiseRowData(data);
        setStockAllocationModalOpen(true);
      }
    } catch(error: any){
      setGenericModalProps(
        getGenericModalProps("error","Stock Data has changed. Please refresh.")
      )
    }
  };


  const calculateSummaryData = (stockData: StockAllocationRowData[]) => {
    // Unique outlets (orders)
    const totalOrders = selectedOrders.length ?? 0;

    // One row per SKU
    const totalSKUs = stockData.length;

    // Sum ordered quantities
    const totalOrderedCases = stockData.reduce((sum, item) => sum + item.orderedCases, 0);
    const totalOrderedPieces = stockData.reduce((sum, item) => sum + item.orderedPieces, 0);
    const totalOrderedOthers = stockData.reduce((sum, item) => sum + item.orderedOthers, 0);

    // Remaining (stock exceeding)
    const stockExceedingCases = stockData.reduce((sum, item) => sum + item.stockExceedingCases, 0);
    const stockExceedingPieces = stockData.reduce((sum, item) => sum + item.stockExceedingPieces, 0);
    const stockExceedingOthers =  stockData.reduce((sum, item) => sum + item.stockExceedingOthers, 0);


    // 🔑 Key accounts: unique outlets with isKeyAccount = true
    const keyAccounts = new Set(
      stockData.flatMap(item =>
        item.outletList
          .filter(o => o.isKeyAccount)
          .map(o => o.outletCode)
      )
    ).size;
    setAccordionOrderData(
      {
        totalOrders,
        totalSKUs,
        totalOrderedCases,
        totalOrderedPieces,
        totalOrderedOthers,
        stockExceedingCases,
        stockExceedingPieces,
        stockExceedingOthers,
        keyAccounts
      }
    )
  };

  useEffect(() => {
    calculateSummaryData(manageStockAllocationRows)
  }, [manageStockAllocationRows])


  const { multipleCategoryFilterEnabled } = ConfigDictionary;

const filteredData = useMemo(() => {


  const labelToIdMap = new Map<string, string>();
  orderTrackingStagesList.forEach((stage) => {
    if (stage?.dmsLabel && stage?.id) {
      labelToIdMap.set(stage.dmsLabel.toUpperCase(), stage.id);
    }
  });

  const hasUserSelectedFilters =
    Object.keys(selectedFilters).length > 0 &&
    Object.values(selectedFilters).some((arr) => arr.length > 0);

  let finalFilteredData = data;

  if (multipleCategoryFilterEnabled && hasUserSelectedFilters) {
    const updatedSelectedFilters = { ...selectedFilters };

    if (filterKeys.includes("status") && selectedFilters["status"]) {
      let statuses: string[] = [];
      selectedFilters["status"].forEach((status) => {
        statuses = [
          ...statuses,
          ...(labelToIdMap.get(status.toUpperCase())?.split(",").map((label) => label.trim()) ?? []),
        ];
      });
      updatedSelectedFilters["status"] = statuses;
    }

    finalFilteredData = getMultipleFilterBasedData(data, updatedSelectedFilters, filterKeys);
  }

  const searchFilteredData = filterData(
    finalFilteredData,
    searchedValue?.toLowerCase(),
    searchWithStatusFilter,
    hasUserSelectedFilters ? "ALL" : statusFilter
  );

  let filteredResults = searchFilteredData;

  if (multipleCategoryFilterEnabled && hasUserSelectedFilters) {
    filteredResults = finalFilteredData.filter((item: any) =>
      new Set(searchFilteredData).has(item)
    );
  }

  return filteredResults;
}, [data, searchedValue, statusFilter, selectedFilters, filterKeys]);

type Allocation = Record<
  string, // batchCode
  {
    total: number;
    cs: number;
    pi: number;
    oth: number;
  }
>;

type OrderForBulkInvoice = {
  id: string;
  orderNumber: string;
  orderCreationTime: string;
  outletName: string;
  outletCode: string;
  allocation: Allocation;
  totalOrderedCs: number;
  totalOrderedPc: number;
  totalOrderedOth: number;
};


const checkOrderPartialStatus = (order: OrderForBulkInvoice) => {
  //We are checking this when we are sure that the total ordered is more than total allocated
  //If the sku which was previously partially allocated has been manually allocated then it will be fully allocated
  //But If the sku which was previously partially allocated has not been manually allocated then it will be partially allocated, and then entire order will be partially allocated

  const payloadOrder = orderBodyForPayload.manual.find((o: any) => o.orderNumber === order.orderNumber)
  if (!payloadOrder) return true

  let isPartial = false

  for (const [skuCode, allocatedQty] of Object.entries(order.allocation)){
    const isManual = payloadOrder.orderDetails.find((od: any) => od.batchCode === skuCode)
    //if the sku has been manually changed then it will be fully allocated
    if (isManual) continue

    const orderedQty = globalNormalizedStockMap?.get(skuCode)?.orderedQuantityByOrder?.[order.id] ?? 0
    if (orderedQty > allocatedQty.total) {
      isPartial = true
    }
  }
  return isPartial
}

const handleBulkInvoiceModalClick = async () => {
  setBulkInvoiceModalLoader(true);
  if (!completeOrderBody || Object.keys(completeOrderBody).length === 0) {
    setBulkInvoiceModalLoader(false);
    return;
  }

  try {
    const { uomConversion } = ConfigDictionary;

    // Optional: validate allocation (if needed)

    await verifyAllocation(orderBodyForPayload);

    const bulkInvoiceRows: any[] = [];
    let allBatchCodesSet: Set<string> = new Set();

    // Collect all unique batch codes
    for (const ro of selectedOrders) {
      const orderedBatchCodes: string = getBatchCodesWithoutFreeItem(ro["orderDetails"] ?? "") ?? "";
      orderedBatchCodes
        .split(",")
        .map((code) => code.trim())
        .filter(Boolean)
        .forEach((code) => allBatchCodesSet.add(code));
    }

    // Initialize orders map
    const orders: Record<string, OrderForBulkInvoice> = {};
    for (const order of selectedOrders) {
      const orderDetails = order["orderDetails"] ?? [];
      let orderCs = 0, orderOth = 0, orderPc = 0;

      orderDetails.forEach((sku: any) => {
        if (sku?.type === "freeItem") return;
        orderCs += sku.caseQuantity ?? 0;
        orderOth += sku.otherUnitQuantity ?? 0;
        orderPc += sku.pieceQuantity ?? 0;
      });

      if (!orders[order.id]) {
        orders[order.id] = {
          id: order.id,
          orderNumber: order.orderNumber,
          orderCreationTime: order.creationTime,
          outletName: order.outletName,
          outletCode: order.outletCode,
          allocation: {},
          totalOrderedCs: orderCs,
          totalOrderedPc: orderPc,
          totalOrderedOth: orderOth,
        };
      }
    }

    // Iterate each batch code and compute allocations per order
    allBatchCodesSet.forEach((batchCode) => {
      const currentSku = globalNormalizedStockMap?.get(batchCode);
      if (!currentSku) return;

      const product = productCatalogue.current?.[batchCode];
      if (!product) return;

      for (const order of currentSku.orders ?? []) {
        const replacementSkuAllocatedForCurrentOrder = currentSku.replacementSkuAllocated?.[order.id] ?? {};

        const totalReplacementAllocated = Object.values(replacementSkuAllocatedForCurrentOrder).reduce(
          (sum, qty) => sum + (Number(qty) || 0),
          0
        );

        const totalAllocated = (currentSku.mainAllocatedByOrder?.[order.id] ?? 0) + totalReplacementAllocated;

        const totalAllocatedBreakdown = addToUomWiseAllocation(product, totalAllocated, orderUomCheck.current);

        // Save allocation per batch code per order
        orders[order.id].allocation[batchCode] = {
          total: totalAllocated,
          cs: totalAllocatedBreakdown.cs,
          pi: totalAllocatedBreakdown.pi,
          oth: totalAllocatedBreakdown.oth,
        };
      }
    });

    // Convert orders map into bulkInvoiceRows
    for (const orderId in orders) {
      const o = orders[orderId];
      let allocatedCaseQuantity = 0,
        allocatedPieceQuantity = 0,
        allocatedOtherUnitQuantity = 0;

      // Sum all batch allocations for this order
      for (const allocation of Object.values(o.allocation)) {
        allocatedCaseQuantity += allocation.cs ?? 0;
        allocatedPieceQuantity += allocation.pi ?? 0;
        allocatedOtherUnitQuantity += allocation.oth ?? 0;
      }

      let allocationStatus = ""
      let borderColor = ""

      if (allocatedCaseQuantity === o.totalOrderedCs && allocatedPieceQuantity === o.totalOrderedPc && allocatedOtherUnitQuantity === o.totalOrderedOth){
        allocationStatus = "INVOICED"
        borderColor = "#9fcb9a"
      } else if ( allocatedCaseQuantity === 0 && allocatedPieceQuantity === 0 && allocatedOtherUnitQuantity === 0 ){
        allocationStatus = "FAILED"
        borderColor = "#f1bebe"
      } else {
          allocationStatus = "PARTIALLY INVOICED";
          borderColor = "#ffc480";
      }

      bulkInvoiceRows.push({
        id: o.orderNumber,
        orderId: o.orderNumber,
        outletName: o.outletName,
        outletCode: o.outletCode,
        totalCaseQuantity: o.totalOrderedCs,
        totalPieceQuantity: o.totalOrderedPc,
        totalOtherUnitQuantity: o.totalOrderedOth,
        allocatedCaseQuantity,
        allocatedPieceQuantity,
        allocatedOtherUnitQuantity,
        sku: o.allocation, // batchCode to UoM allocation
        bulkStatus: "PENDING",
        allocationStatus,
        borderColor
      });
    }

    setBulkInvoiceModalRows(bulkInvoiceRows);
    setBulkInvoiceModalOpen(true);
  } catch (err: any) {
    console.error("Allocation error:", err);
    setGenericModalProps(getGenericModalProps("error", "Stock Data has changed. Please refresh."));
  } finally {
    setBulkInvoiceModalLoader(false);
  }
};



  useEffect(() => {
    if (!filteredData?.length) {
      setManageStockAllocationRows([]);
      return;
    }

    // Define priority order for statuses
    const priorityOrder: Record<string, number> = {
      "PARTIALLY ALLOCATED": 1,
      "OUT OF STOCK": 2,
      "FULLY ALLOCATED": 3,
    };

    // Sort based on defined order
    const sortedData = [...filteredData].sort((a, b) => {
      const aPriority = priorityOrder[a.status?.toUpperCase()] ?? 999;
      const bPriority = priorityOrder[b.status?.toUpperCase()] ?? 999;
      return aPriority - bPriority;
    });

    setManageStockAllocationRows(sortedData);
  }, [filteredData]);


  // Generate filter lists
  useEffect(() => {
    const counts: Record<string, Record<string, number>> = {};
    filterKeys.forEach(key => {
      counts[key] = {};
    });

    if (filterKeys.includes("status")) {
      const labelToIdMap = new Map<string, string>();
      let allDmsLabel: string | undefined;
      orderTrackingStagesList.forEach((stage) => {
        if (stage?.dmsLabel && stage?.id) {
          labelToIdMap.set(stage.dmsLabel.toUpperCase(), stage.id);
          if (stage.id === "ALL") {
            allDmsLabel = stage.dmsLabel.toUpperCase();
          }
        }
      });

      orderTrackingStagesList.forEach((stage: any) => {
        if (stage?.dmsLabel && stage.dmsLabel.toUpperCase() !== allDmsLabel) {
          counts["status"][stage.dmsLabel.toUpperCase()] = 0;
        }
      });

      data.forEach((row: any) => {
        const rowStatus = row?.status?.toUpperCase();
        Object.keys(counts["status"]).forEach((key) => {
          const labels = labelToIdMap.get(key)?.split(',').map((label) => label.trim()) ?? [];
          if (labels.includes(rowStatus)) {
            counts["status"][key] += 1;
          }
        });
      });
    }

    setMultipleFiltersList(counts);
  }, [data]);

  // Event handlers
  const handleSearchValueChange = (newValue: string) => {
    setSearchedValue(newValue);
  };

  const handleStatusFilterChange = (newFilter: string) => {
    setStatusFilter(newFilter);
  };

  const handleFilterChange = (filterType: string, value: string) => {
    if (filterType === "allocationStatus") {
      setStatusFilter(value);
    }
    setSelectedFilters(prev => ({
      ...prev,
      [filterType]: [value]
    }));
  };



  const handleFilterSearchValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterSearchValue(e.target.value);
    e.stopPropagation();
  };

  const handleSelectedFilterChange = (key: string, value: string) => {
    setSelectedFilters(prev => {
      const current = prev[key] || [];
      return {
        ...prev,
        [key]: current.includes(value)
          ? current.filter(v => v !== value)
          : [...current, value],
      };
    });
  };

  const handleFilterTypeChange = (type: string) => {
    setSelectedFilterType(type);
    setFilterSearchValue("");
  };

  const handleSnackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleSetCheckedIdArray = (ids: any[]) => {
    setCheckedRows(ids);
  };

  const handleRedirect = (url: string, rowData: any) => {
    // Handle row click - for now just log
    console.log("Row clicked:", rowData);
  };

  const handleBulkAllocation = () => {
    console.log("Bulk allocation clicked for:", checkedRows);
    setSnackbarOpen(true);
  };
    const [textSwitcherItems, setTextSwitcherItems] = useState<TextSwitcherItemObject[]>([]);
  
  const getTextSwitcherItems = () => {

    setTextSwitcherItems([
      ...(
        [{
        label: "Please Note:",
        text: `Manually allocate SKUs to outlets, then review and adjust in the allocation method`,
        itemStyles: {
          container: {
            backgroundColor: "#FFF1FE80",
          },
          label: {},
          text: {
            color: "#7B087B"
          }
        },
      }]
      ),
    ]);
  }

  const sortOversold = () => {
    setSelectedFilters({});
    setFilterSearchValue("");
    handleSelectedFilterChange("status", "OUT OF STOCK")
    handleSelectedFilterChange("status", "PARTIALLY ALLOCATED")
  }

  useEffect(() => {
    getTextSwitcherItems()
  }, [])
  const [selectedOrdersAfterInvoicing, setSelectedOrdersAfterInvoicing] = useState<any>(selectedOrders);
  useEffect(() => {
    setSelectedOrdersAfterInvoicing(selectedOrders)
  }, [selectedOrders])

  const handleBulkInvoiceModalClose = () => {
    setBulkInvoiceModalOpen(false);

    if (selectedOrdersAfterInvoicing?.length === selectedOrders?.length) {
      setBulkInvoiceModalOpen(false);
      return;
    }

    if (selectedOrdersAfterInvoicing?.length === 0) {
      navigate("/dashboard/sale/manageOrders", { replace: true });
    } else {
      navigate("/dashboard/sale/manageStockAllocation", {
        replace: true,
        state: { selectedOrders: selectedOrdersAfterInvoicing },
      });
    }
  };

  useEffect(() => {
    const hasPendingManual = manageStockAllocationRows.some(
      (row) => row?.allocationMethod?.toUpperCase() === "MANUAL" && (!row?.hasCompletedAllocation)
    );
    setIsAnyManualAndPending(hasPendingManual);
  }, [manageStockAllocationRows]);


  return (
     <div className="manage_order_parentV2">
          <ScreenOverlay apply={loader} />
    
          <CustomGenericModal
            type={genericModalProps?.type}
            message={genericModalProps?.message}
            leftButtonText={genericModalProps?.leftButtonText}
            modalOpen={genericModalProps?.open}
            onClose={() => setGenericModalProps(initialGenericModalProps)}
          />
  
    

          <div className="view_order_header_with_search" style={{alignItems: "flex-start", marginBottom: "8px", position: "relative"}}>
            <div className="view_order_header">
              <FormHeader
                parentLabel={
                  "Manage Orders"
                }
                label={"Review Stocks for selected orders"}
                goBackToParent={() => {
                  navigate(-1)
                }
              }
              />
            </div>
            <div className="view_order_manage_order_search_bar">
              
              <SearchV2 searchFunction={handleSearchValueChange} />
            </div>
         
          </div>
          <div>
            <CustomAccordianV2
                metaData1={getMetaData1(sortOversold)}
                data1={accordionOrderData}
                showMoreDetails={false}
            />
          </div>
    
          <div style={{display: "flex", justifyContent: "space-between", marginTop: "9px"}}>
            <div className="stock_allocation_heading">
              <div className="HeaderV2_label_bar" />
              SKU details
            </div>
            <TextSwitcher textSwitcherItems={textSwitcherItems ?? []} interval={3000}/>
            <div style={{display: "flex", gap: "4px", height: "2.6em", width: "100%", justifyContent: "end", maxWidth: "500px"}}>
              <div className="order-status-filters">
                <div
                  className="order-status-button"
                  onClick={() => {
                    setSelectedFilters({});
                    setFilterSearchValue("");
                  }}
                  style={{
                    backgroundColor: Object.values(selectedFilters ?? {}).some(
                      (arr) => arr?.length > 0
                    )
                      ? "#FAFAFB" : "#00A392",
                  }}
                >
                  <span style={{ 
                    color: Object.values(selectedFilters ?? {}).some(
                      (arr) => arr?.length > 0
                    )
                    ? "#7c7c7c" :  "white",
                    lineHeight: 1
                  }}>All</span>
                </div>
                {Object.keys(multipleFiltersList["status"] ?? {})
                  .slice(0, 4)
                  .filter((value) =>
                    filterSearchValue && typeof value === "string"
                      ? value.toLowerCase().includes(filterSearchValue.toLowerCase())
                      : true
                  )
                  .map((value) => (
                    <div
                      className="order-status-button"
                      onClick={() => handleSelectedFilterChange("status", value)}
                      style={{
                        backgroundColor: (selectedFilters["status"] || []).includes(value)
                          ? "#00A392"
                          : "#FAFAFB",
                      }}
                    >
                      <span style={{ color: (selectedFilters["status"] || []).includes(value) ? "white" : "#7c7c7c", lineHeight: 1,   }}>{capitalizeFirstLetter(value)}</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
          
          {loader ? (
            <Loader sx={{ position: "absolute", top: "55%" }} />
          ) : (
            <CustomDataGrid
              key={"manageOrder"}
              rows={Array.isArray(manageStockAllocationRows) ? manageStockAllocationRows : []}
              columnsMetaData={getStockAllocationColumnsMetaData(handleDropDownSelect)}
              orderStatusConfig={orderTrackingStagesList}
              tableWidth={0.78}
              enableRowClickRedirect={true}
              insideManualStockAllocation={true}
              onRowClick={handleRowClick}
              handleBulkInvoiceModalClick={handleBulkInvoiceModalClick}
              footerButtonLoading={bulkInvoiceModalLoader}
              isAnyManualAndPending={isAnyManualAndPending}
            />
          )}
          <OutletWiseAllocationModal
            statusConfig={orderTrackingStagesList}
            productData={skuWiseData}
            rowData={skuWiseRowData}
            open={stockAllocationModalOpen}
            onClose={() => setStockAllocationModalOpen(false)}
            setManageStockAllocationRows={setManageStockAllocationRows}
            setOrderBodyForPayload={setOrderBodyForPayload}
            orderBodyForPayload={orderBodyForPayload}
            handleReset={handleReset}
            setCompleteOrderBody={setCompleteOrderBody}
            completeOrderBody={completeOrderBody}
            setManageStockAllocationData={setData}
            orderUomCheck={orderUomCheck.current}
            globalNormalizedStockMap={globalNormalizedStockMap}
            products={productCatalogue.current}
            setGlobalNormalizedStockMap={setGlobalNormalizedStockMap}
          />
          <BulkInvoiceModal
            open={bulkInvoiceModalOpen}
            onClose={handleBulkInvoiceModalClose}
            rows={bulkInvoiceModalRows}
            totalOrderValue={1000}
            totalOrders={10}
            orderBodyForPayload={orderBodyForPayload}
            bulkOrderTrackingStages={bulkOrderTrackingStages}
            selectedOrders={selectedOrders}
            setSelectedOrdersAfterInvoicing={setSelectedOrdersAfterInvoicing}
          />

        </div>
  );
};

export default ManageStockAllocation;
